import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Home,
  Briefcase,
  Users,
  Calendar,
  BarChart3,
  MessageSquare,
  Search,
  Plus,
  ChevronDown,
  Download,
  UserPlus,
  Code,
  Menu,
  X,
  Video,
  CreditCard
} from 'lucide-react'
import NotificationBell from './NotificationBell'
import UserMenu from './UserMenu'
import CreditBalanceWidget from '@/components/credits/CreditBalanceWidget'
import { apiClient } from '@/lib/api'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'

interface RecruiterHeaderProps {
  onScheduleInterview?: () => void
  onCreatePosition?: () => void
}

export default function RecruiterHeaderNew({ onScheduleInterview, onCreatePosition }: RecruiterHeaderProps) {
  const location = useLocation()
  const pathname = location.pathname
  const [showQuickActions, setShowQuickActions] = useState(false)
  const [showSearchResults, setShowSearchResults] = useState(false)
  const [showMobileMenu, setShowMobileMenu] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<any[]>([])
  const [searchLoading, setSearchLoading] = useState(false)
  const moreMenuRef = useRef<HTMLDivElement>(null)
  const codingChallengesEnabled = useFeatureFlag('recruiterCodingChallenges')
  const messagesEnabled = useFeatureFlag('recruiterMessages')
  const teamsEnabled = useFeatureFlag('recruiterTeams')

  const handleSearch = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([])
      setShowSearchResults(false)
      return
    }

    try {
      setSearchLoading(true)
      
      const [candidatesResponse, positionsResponse] = await Promise.all([
        apiClient.getCandidates({ search: query.trim(), limit: 5 }),
        apiClient.getPositions({ search: query.trim(), limit: 5 })
      ])

      const results = []
      
      if (candidatesResponse?.data?.candidates) {
        results.push(...candidatesResponse.data.candidates.map((candidate: any) => ({
          id: candidate.id,
          type: 'candidate',
          title: candidate.name || `${candidate.first_name} ${candidate.last_name}`,
          subtitle: candidate.position || candidate.current_title || 'Candidate',
          description: candidate.email,
          href: `/recruiter/candidates/${candidate.id}`
        })))
      }

      if (positionsResponse?.data?.positions) {
        results.push(...positionsResponse.data.positions.map((position: any) => ({
          id: position.id,
          type: 'position',
          title: position.title,
          subtitle: position.department || 'Position',
          description: position.location || position.salary_range || '',
          href: `/recruiter/positions/${position.id}`
        })))
      }

      setSearchResults(results)
      setShowSearchResults(true)
    } catch (error) {
      console.error('Search failed:', error)
      setSearchResults([])
    } finally {
      setSearchLoading(false)
    }
  }

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handleSearch(searchQuery)
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [searchQuery])

  // Close "More" dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target as Node)) {
        setShowQuickActions(false)
      }
    }

    if (showQuickActions) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showQuickActions])

  // Close "More" dropdown when pathname changes (navigation occurs)
  useEffect(() => {
    setShowQuickActions(false)
  }, [pathname])

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
  }

  const handleSearchResultClick = (result: any) => {
    setSearchQuery('')
    setSearchResults([])
    setShowSearchResults(false)
    window.location.href = result.href
  }

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setSearchQuery('')
      setSearchResults([])
      setShowSearchResults(false)
    } else if (e.key === 'Enter' && searchQuery.trim() && searchResults.length === 0) {
      console.log('Full search for:', searchQuery)
    }
  }

  const handleSearchBlur = () => {
    setTimeout(() => {
      setShowSearchResults(false)
    }, 200)
  }

  const handleSearchFocus = () => {
    if (searchQuery.trim() && searchResults.length > 0) {
      setShowSearchResults(true)
    }
  }

  const primaryNavigation = [
    { name: 'Dashboard', href: '/recruiter/dashboard', icon: Home },
    { name: 'Positions', href: '/recruiter/positions', icon: Briefcase },
    { name: 'Candidates', href: '/recruiter/candidates', icon: Users },
    ...(teamsEnabled ? [{ name: 'Teams', href: '/recruiter/teams', icon: UserPlus }] : []),
    { name: 'Interviews', href: '/recruiter/interviews', icon: Video },
  ]

  const secondaryNavigation = [
    { name: 'Credits', href: '/recruiter/credits', icon: CreditCard },
    ...(codingChallengesEnabled ? [{ name: 'Coding', href: '/recruiter/coding-challenges', icon: Code }] : []),
    ...(messagesEnabled ? [{ name: 'Messages', href: '/recruiter/messages', icon: MessageSquare }] : []),
  ]

  const allNavigation = [...primaryNavigation, ...secondaryNavigation]

  const quickActions = []

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="px-2 sm:px-4 lg:px-8">
        <div className="flex justify-between items-center py-2 sm:py-3 lg:py-4 gap-2 sm:gap-4">
          {/* Logo and Company */}
          <div className="flex items-center space-x-1.5 sm:space-x-2 lg:space-x-4 flex-shrink-0">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">AI</span>
              </div>
            </div>
            <div className="hidden sm:block">
              <h1 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-900 whitespace-nowrap">AI Recruiter</h1>
              <p className="text-xs lg:text-sm text-gray-500 hidden md:block whitespace-nowrap">Talent Management</p>
            </div>
          </div>

          {/* Main Navigation */}
          <nav className="hidden lg:flex space-x-6">
            {primaryNavigation.map((item) => {
              const Icon = item.icon
              const isActive = pathname.startsWith(item.href)
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setShowQuickActions(false)}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors relative ${
                    isActive
                      ? 'text-blue-600 bg-blue-50'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden xl:block">{item.name}</span>
                  {isActive && (
                    <motion.div
                      layoutId="activeTab"
                      className="absolute inset-0 bg-blue-50 rounded-md -z-10"
                      initial={false}
                      transition={{ type: "spring", duration: 0.3 }}
                    />
                  )}
                </Link>
              )
            })}
            
            {/* More Menu */}
            {(secondaryNavigation.length > 0 || quickActions.length > 0) && (
              <div className="relative" ref={moreMenuRef}>
                <button
                  onClick={() => setShowQuickActions(!showQuickActions)}
                  className="flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden xl:block">More</span>
                  <ChevronDown className="w-3 h-3" />
                </button>
                <AnimatePresence>
                  {showQuickActions && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95, y: -10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: -10 }}
                      className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
                    >
                      {secondaryNavigation.map((item) => {
                        const Icon = item.icon
                        const isActive = pathname.startsWith(item.href)
                        return (
                          <Link
                            key={item.name}
                            to={item.href}
                            onClick={() => setShowQuickActions(false)}
                            className={`w-full flex items-center px-4 py-2 text-sm hover:bg-gray-50 ${
                              isActive ? 'text-blue-600 bg-blue-50' : 'text-gray-700'
                            }`}
                          >
                            <Icon className="w-4 h-4 mr-3" />
                            {item.name}
                          </Link>
                        )
                      })}
                      {quickActions.length > 0 && (
                        <>
                          <div className="border-t border-gray-100 my-1"></div>
                          {quickActions.map((action) => {
                            const Icon = action.icon
                            return (
                              <button
                                key={action.name}
                                onClick={() => {
                                  action.onClick?.()
                                  setShowQuickActions(false)
                                }}
                                className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                              >
                                <Icon className="w-4 h-4 mr-3" />
                                {action.name}
                              </button>
                            )
                          })}
                        </>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            className="lg:hidden p-1.5 sm:p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors flex-shrink-0"
          >
            {showMobileMenu ? <X className="w-5 h-5 sm:w-6 sm:h-6" /> : <Menu className="w-5 h-5 sm:w-6 sm:h-6" />}
          </button>

          {/* Right side actions */}
          <div className="flex items-center space-x-1 sm:space-x-2 lg:space-x-4">
            {/* Search */}
            <div className="hidden lg:block">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                  onKeyDown={handleSearchKeyDown}
                  onFocus={handleSearchFocus}
                  onBlur={handleSearchBlur}
                  placeholder="Search..."
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-48 xl:w-64"
                />
                
                {/* Search Results Dropdown */}
                <AnimatePresence>
                  {showSearchResults && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95, y: -10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: -10 }}
                      className="absolute top-full mt-2 w-full bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 max-h-80 overflow-y-auto"
                    >
                      {searchLoading ? (
                        <div className="px-4 py-3 text-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mx-auto"></div>
                          <p className="text-sm text-gray-500 mt-2">Searching...</p>
                        </div>
                      ) : searchResults.length > 0 ? (
                        <>
                          {searchResults.map((result) => (
                            <button
                              key={`${result.type}-${result.id}`}
                              onClick={() => handleSearchResultClick(result)}
                              className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                            >
                              <div className="flex items-center space-x-3">
                                {result.type === 'candidate' ? (
                                  <Users className="w-4 h-4 text-blue-600" />
                                ) : (
                                  <Briefcase className="w-4 h-4 text-green-600" />
                                )}
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-gray-900">{result.title}</p>
                                  <p className="text-xs text-gray-600">{result.subtitle}</p>
                                  {result.description && (
                                    <p className="text-xs text-gray-500">{result.description}</p>
                                  )}
                                </div>
                                <span className="text-xs text-gray-400 capitalize">{result.type}</span>
                              </div>
                            </button>
                          ))}
                          <div className="px-4 py-2 border-t border-gray-200">
                            <p className="text-xs text-gray-500">
                              Press Enter to search for "{searchQuery}"
                            </p>
                          </div>
                        </>
                      ) : searchQuery.trim() && !searchLoading ? (
                        <div className="px-4 py-3 text-center">
                          <p className="text-sm text-gray-500">No results found for "{searchQuery}"</p>
                        </div>
                      ) : null}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* Mobile Search Button */}
            <button className="lg:hidden p-1.5 sm:p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0">
              <Search className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>

            {/* Credit Balance */}
            <CreditBalanceWidget compact={true} showHistory={false} linkTo="/recruiter/credits" />

            {/* Notifications */}
            <NotificationBell />

            {/* User Menu */}
            <UserMenu userType="recruiter" />
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      <AnimatePresence>
        {showMobileMenu && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden border-t border-gray-200 bg-white"
          >
            <div className="px-4 py-4 space-y-1">
              {allNavigation.map((item) => {
                const Icon = item.icon
                const isActive = pathname.startsWith(item.href)
                
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => {
                      setShowMobileMenu(false)
                      setShowQuickActions(false)
                    }}
                    className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? 'text-blue-600 bg-blue-50'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{item.name}</span>
                  </Link>
                )
              })}
              
              {/* Mobile Search */}
              <div className="pt-4 border-t border-gray-200">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={handleSearchInputChange}
                    onKeyDown={handleSearchKeyDown}
                    onFocus={handleSearchFocus}
                    onBlur={handleSearchBlur}
                    placeholder="Search candidates, positions..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              
              {/* Mobile Quick Actions */}
              {quickActions.length > 0 && (
                <div className="pt-4 border-t border-gray-200">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Quick Actions</p>
                  <div className="space-y-1">
                    {quickActions.map((action) => {
                      const Icon = action.icon
                      return (
                        <button
                          key={action.name}
                          onClick={() => {
                            action.onClick?.()
                            setShowMobileMenu(false)
                          }}
                          className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                        >
                          <Icon className="w-4 h-4" />
                          {action.name}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bottom Tab Navigation for Mobile */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-40">
        <div className="flex justify-around py-2">
          {primaryNavigation.map((item) => {
            const Icon = item.icon
            const isActive = pathname.startsWith(item.href)
            
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`flex flex-col items-center space-y-1 px-2 py-1 rounded-lg transition-colors min-w-0 ${
                  isActive
                    ? 'text-blue-600'
                    : 'text-gray-600'
                }`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                <span className="text-xs font-medium truncate">{item.name}</span>
              </Link>
            )
          })}
        </div>
      </div>
    </header>
  )
}
